import { useCallback, useEffect, useRef } from 'react';

export type DrillFocusMemo = { path: string; id: number | string; index: number };

/**
 * Remembers WHICH row the user opened when they left a report for another route
 * — Balance Sheet → a ledger — so that coming back puts the keyboard cursor
 * on that same ledger instead of the top of the list.
 *
 * Companion to useDrillPath: that restores the drill DEPTH (which is durable
 * and belongs in the URL), this restores the CURSOR within it.
 *
 * Why sessionStorage rather than the URL: the write has to happen in the same
 * tick as navigate(), and a setSearchParams() update is applied asynchronously
 * — it would race the unmount and be lost. sessionStorage writes immediately,
 * and a cursor position is per-tab throwaway state that has no business being
 * in a shareable link anyway.
 *
 * The memo is tagged with the drill path it was taken at, so it is only ever
 * applied at the same depth — a stale memo from another branch is ignored
 * rather than parking the cursor on an unrelated row.
 */
export default function useDrillFocus(scope: string) {
  const KEY = `drill-focus:${scope}`;
  const memo = useRef<DrillFocusMemo | null>(null);
  const hasRead = useRef(false);

  // Read once per mount, and clear immediately — the memo belongs to this one
  // return trip. The ref guard matters: StrictMode invokes mount effects twice
  // in development, and without it the second pass would find nothing and wipe
  // what the first pass captured.
  useEffect(() => {
    if (hasRead.current) return;
    hasRead.current = true;
    try {
      const raw = sessionStorage.getItem(KEY);
      if (!raw) return;
      sessionStorage.removeItem(KEY);
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.path === 'string') memo.current = parsed as DrillFocusMemo;
    } catch {
      memo.current = null;
    }
  }, [KEY]);

  /** Call immediately before navigating away from row `index` (id `id`). */
  const remember = useCallback((path: string, id: number | string, index: number) => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ path, id, index }));
    } catch { /* private mode / quota — the cursor just won't restore */ }
  }, [KEY]);

  /** Take the memo if it was recorded at this depth; null once consumed. */
  const take = useCallback((path: string): DrillFocusMemo | null => {
    const m = memo.current;
    if (!m || m.path !== path) return null;
    memo.current = null;
    return m;
  }, []);

  return { remember, take };
}
